import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useUser } from '@/hooks/useUser';
import { PageWrapper } from '@/layout/components/PageWrapper';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InboxListTab } from './components/InboxListTab';
import { EmailsTab } from './components/EmailsTab';
import { AgentsTab } from './components/AgentsTab';
import { AutomationTab } from './components/AutomationTab';
import { Bot, Mail, Settings, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useGetInboxListQuery } from '@/store/emailInboxApi';
import { InboxFormModal } from './components/InboxFormModal';
import { EmailInboxConfig } from '@/types/EmailInbox';

export function InboxManagementPage() {
  const { user } = useUser();
  const [activeInboxId, setActiveInboxId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'inboxes' | 'emails' | 'agents' | 'automation'>('inboxes');
  const [editInbox, setEditInbox] = useState<EmailInboxConfig | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: inboxes = [] } = useGetInboxListQuery(undefined, { skip: !user?.is_superuser });

  // Handle OAuth return: ?oauth=success&inbox_id=<id>  or  ?oauth=error&reason=<reason>
  useEffect(() => {
    const oauthStatus = searchParams.get('oauth');
    const inboxIdParam = searchParams.get('inbox_id');
    const reason = searchParams.get('reason');

    if (!oauthStatus) return;

    // Clear URL params immediately
    setSearchParams({}, { replace: true });

    if (oauthStatus === 'success' && inboxIdParam) {
      const inboxId = Number(inboxIdParam);
      toast.success('Gmail חובר בהצלחה! בחר Labels לניטור.');
      // Open edit modal for the (possibly newly created) inbox
      // Inboxes list will refetch on next render; find it or wait
      setEditOpen(true);
      setEditInbox(inboxes.find((i) => i.id === inboxId) ?? { id: inboxId } as EmailInboxConfig);
      setActiveTab('inboxes');
    } else if (oauthStatus === 'error') {
      toast.error(`שגיאה בחיבור Gmail${reason ? `: ${decodeURIComponent(reason)}` : ''}`);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-populate editInbox when inboxes list loads after OAuth return
  useEffect(() => {
    if (editOpen && editInbox?.id && inboxes.length > 0) {
      const found = inboxes.find((i) => i.id === editInbox.id);
      if (found && found !== editInbox) {
        setEditInbox(found);
      }
    }
  }, [inboxes]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user?.is_superuser) {
    return (
      <PageWrapper>
        <div className="text-center py-20 text-gray-500">
          <p className="text-xl">גישה מוגבלת לסופר יוזר בלבד</p>
        </div>
      </PageWrapper>
    );
  }

  function handleViewEmails(inboxId: number) {
    setActiveInboxId(inboxId);
    setActiveTab('emails');
  }

  function handleEditInbox(inbox: EmailInboxConfig) {
    setEditInbox(inbox);
    setEditOpen(true);
  }

  return (
    <PageWrapper>
      <div className="flex items-center gap-3 mb-6">
        <Mail className="h-6 w-6 text-blue-600" />
        <h1 className="text-2xl font-semibold">ניהול תיבות מייל מנוטרות</h1>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'inboxes' | 'emails' | 'agents' | 'automation')}>
        <TabsList className="mb-4">
          <TabsTrigger value="inboxes" className="gap-2">
            <Settings className="h-4 w-4" />
            תיבות מנוטרות
          </TabsTrigger>
          <TabsTrigger value="emails" className="gap-2">
            <Mail className="h-4 w-4" />
            מיילים נכנסים
          </TabsTrigger>
          <TabsTrigger value="agents" className="gap-2">
            <Users className="h-4 w-4" />
            נציגים
          </TabsTrigger>
          <TabsTrigger value="automation" className="gap-2">
            <Bot className="h-4 w-4" />
            אוטומציה
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inboxes">
          <InboxListTab onViewEmails={handleViewEmails} onEdit={handleEditInbox} />
        </TabsContent>

        <TabsContent value="emails">
          <EmailsTab inboxId={activeInboxId} onInboxChange={setActiveInboxId} />
        </TabsContent>

        <TabsContent value="agents">
          <AgentsTab />
        </TabsContent>

        <TabsContent value="automation">
          <AutomationTab />
        </TabsContent>
      </Tabs>

      {/* Edit modal – also opened automatically after OAuth return */}
      <InboxFormModal
        open={editOpen}
        onOpenChange={(v) => { setEditOpen(v); if (!v) setEditInbox(null); }}
        initialData={editInbox}
      />
    </PageWrapper>
  );
}
